import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  useBillingCostReport,
  useBillingPricingConfig,
  type BillingReportGroupBy,
} from '../../services/billing.service';
import { PackEstimateUnitsPanel } from '../../components/admin/pricing/PackEstimateUnitsPanel';
const day = (date: Date) => date.toISOString().slice(0, 10);
const money = (value: number | null) => (value === null ? '—' : `$${value.toFixed(4)}`);
export const BillingCostReportPage: React.FC = () => {
  const initial = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    return { from: day(from), to: day(to) };
  }, []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [groupBy, setGroupBy] = useState<BillingReportGroupBy>('model');
  const report = useBillingCostReport({ from, to, groupBy });
  const pricing = useBillingPricingConfig();
  const totals = report.data?.totals;
  const overheadAlert = Boolean(
    totals?.overheadRatio !== null &&
    totals?.overheadRatio !== undefined &&
    pricing.data &&
    totals.overheadRatio > pricing.data.overheadFactor
  );
  return (
    <Box className="space-y-5 p-8">
      <Typography variant="h4" fontWeight={700}>
        Billing Cost Report
      </Typography>
      <Box className="flex flex-wrap gap-3">
        <TextField
          label="From"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Group by"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as BillingReportGroupBy)}
        >
          {['model', 'capability', 'day'].map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {report.isLoading && <CircularProgress />}
      {report.error && (
        <Alert severity="error">
          {(report.error as Error).message ||
            'Unable to load cost report. Check the selected date range.'}
        </Alert>
      )}
      {totals && (
        <Box className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Calls', totals.calls.toLocaleString()],
            ['Catalog cost', money(totals.costUsdCatalog)],
            ['Gateway cost', money(totals.gatewayCostUsd)],
            [
              'Overhead ratio',
              totals.overheadRatio === null ? '—' : `${totals.overheadRatio.toFixed(2)}×`,
            ],
            ['Rows without catalog cost', totals.rowsWithoutCatalogCost],
            ['Calls without user', totals.usersNullCalls],
          ].map(([label, value]) => (
            <Card key={label}>
              <CardContent>
                {label === 'Overhead ratio' ? (
                  <Tooltip title="Overhead ratio = gateway cost ÷ catalog cost.">
                    <Typography color="text.secondary" className="w-fit cursor-help">
                      {label}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography color="text.secondary">{label}</Typography>
                )}
                <Typography
                  variant="h6"
                  color={
                    label === 'Overhead ratio'
                      ? overheadAlert
                        ? 'error.main'
                        : 'success.main'
                      : undefined
                  }
                >
                  {value}
                </Typography>
                {label === 'Overhead ratio' && overheadAlert && (
                  <Typography variant="caption" color="error.main">
                    above assumption
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      {overheadAlert && (
        <Alert severity="error">
          Actual overhead exceeds the configured factor ({pricing.data?.overheadFactor}×).
        </Alert>
      )}
      {report.data && (
        <Box className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {[
                  'Group',
                  'Calls',
                  'Tokens in/out',
                  'Credits charged',
                  'Catalog $',
                  'Gateway $',
                  'Variance $ / %',
                  'Status',
                ].map((x) => (
                  <th className="p-3" key={x}>
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.data.rows.map((row, index) => (
                <tr
                  className="border-t border-slate-200 dark:border-slate-700"
                  key={`${row.model}-${row.capabilityCode}-${row.day}-${index}`}
                >
                  <td className="p-3">
                    {groupBy === 'model'
                      ? row.model
                      : groupBy === 'capability'
                        ? row.capabilityCode
                        : row.day}
                  </td>
                  <td className="p-3">{row.calls}</td>
                  <td className="p-3">
                    {row.promptTokens.toLocaleString()} / {row.completionTokens.toLocaleString()}
                  </td>
                  <td className="p-3">{row.creditsCharged.toLocaleString()}</td>
                  <td className="p-3">{money(row.costUsdCatalog)}</td>
                  <td className="p-3">{money(row.gatewayCostUsd)}</td>
                  <td className="p-3">
                    {money(row.varianceUsd)} /{' '}
                    {row.variancePercent === null ? '—' : `${row.variancePercent.toFixed(1)}%`}
                  </td>
                  <td className="p-3">
                    {row.flagged ? (
                      <span className="rounded bg-red-100 px-2 py-1 text-red-700">Flagged</span>
                    ) : (
                      'OK'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
      <PackEstimateUnitsPanel />
    </Box>
  );
};
